// =====================================================================
// API client. Talks to the FastAPI backend when REACT_APP_BACKEND_URL is set
// (production / Emergent). When it's empty, falls back to a localStorage "demo"
// adapter so the UI is fully runnable without the backend (local preview).
// Both paths return the SAME shapes (see API_CONTRACT.md).
// =====================================================================
import axios from 'axios';

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
export const DEMO = !BACKEND;
const TOKEN_KEY = 'lpa_token';

// Auth token lives in sessionStorage (narrower XSS/persistence surface than
// localStorage). One-time migration clears any token from older builds.
try {
  const legacy = localStorage.getItem(TOKEN_KEY);
  if (legacy) {
    if (!sessionStorage.getItem(TOKEN_KEY)) sessionStorage.setItem(TOKEN_KEY, legacy);
    localStorage.removeItem(TOKEN_KEY);
  }
} catch (e) { console.warn('Token storage unavailable', e); }

export function getToken() { return sessionStorage.getItem(TOKEN_KEY) || ''; }
export function setToken(t) { t ? sessionStorage.setItem(TOKEN_KEY, t) : sessionStorage.removeItem(TOKEN_KEY); }

// Active data region (AU/US). Sent on every request as X-Region so the backend
// can scope reads/writes. Set by App whenever the region changes.
let currentRegion = 'AU';
export function setApiRegion(r) { currentRegion = r || 'AU'; }

const http = axios.create({ baseURL: BACKEND + '/api' });
http.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = 'Bearer ' + t;
  cfg.headers['X-Region'] = currentRegion;
  return cfg;
});

// ---------- DEMO (localStorage) adapter ----------
const LS = {
  get(k, d) { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } },
  set(k, v) { localStorage.setItem(k, JSON.stringify(v)); },
};
const DKEY = { products: 'lpa_demo_products', projects: 'lpa_demo_projects', users: 'lpa_demo_users', allowed: 'lpa_demo_allowed', audit: 'lpa_demo_audit', user: 'lpa_demo_user' };
const uid = (p) => p + Math.random().toString(36).slice(2, 10);
const now = () => new Date().toISOString();

const DEMO_USERS = [
  { id: 'usr_admin', email: 'admin@local', name: 'Master Admin (demo)', role: 'master_admin' },
  { id: 'usr_sales', email: 'sales@local', name: 'Sales & Tech (demo)', role: 'sales_tech' },
  { id: 'usr_guest', email: 'guest@local', name: 'Guest (demo)', role: 'guest' },
];
function demoSeed() {
  if (!LS.get(DKEY.users)) LS.set(DKEY.users, DEMO_USERS);
  if (!LS.get(DKEY.allowed)) LS.set(DKEY.allowed, DEMO_USERS.map(u => ({ email: u.email, role: u.role, name: u.name })));
  if (!LS.get(DKEY.products)) LS.set(DKEY.products, []);
  if (!LS.get(DKEY.projects)) LS.set(DKEY.projects, []);
  if (!LS.get(DKEY.audit)) LS.set(DKEY.audit, []);
}
demoSeed();
function demoUser() { return LS.get(DKEY.user, null); }
function demoAudit(action, entity_type, entity_id, metadata) {
  const u = demoUser(); if (!u) return;
  const a = LS.get(DKEY.audit, []);
  a.push({ id: uid('au_'), user_id: u.id, user_email: u.email, user_name: u.name, user_role: u.role, action, entity_type, entity_id, metadata, created_at: now() });
  LS.set(DKEY.audit, a.slice(-2000));
}
function searchText(p) {
  const d = p.data || {};
  const models = (d.variants || []).map(v => (v && v.model) || '').join(' ');
  return [p.name, p.code, d.variant, models].join(' ').toLowerCase();
}

// in-memory legacy datasheets for demo (files are large; session-only in demo)
let demoLegacy = [];

export const api = {
  demo: DEMO,

  // ---- auth ----
  async login(email, password) {
    if (DEMO) {
      const u = (LS.get(DKEY.users, DEMO_USERS)).find(x => x.email === email) || DEMO_USERS[0];
      LS.set(DKEY.user, u); setToken('demo.' + u.id);
      demoAudit('auth.login', 'user', u.id, { mode: 'demo' });
      return { token: getToken(), user: u };
    }
    const { data } = await http.post('/auth/login', { email, password });
    setToken(data.token); return data;
  },
  async loginDemoAs(role) { // demo-only role picker
    const u = DEMO_USERS.find(x => x.role === role) || DEMO_USERS[0];
    LS.set(DKEY.user, u); setToken('demo.' + u.id);
    demoAudit('auth.login', 'user', u.id, { mode: 'demo' });
    return { token: getToken(), user: u };
  },
  async microsoft(ms_id_token) {
    const { data } = await http.post('/auth/microsoft', { ms_id_token });
    setToken(data.token); return data;
  },
  async me() {
    if (DEMO) { const u = demoUser(); if (!u) throw new Error('no session'); return { user: u }; }
    const { data } = await http.get('/auth/me'); return data;
  },
  logout() { if (DEMO) LS.set(DKEY.user, null); setToken(''); },

  // ---- AI import ----
  async aiExtractDatasheet(fileList, onStage) {
    const fd = new FormData();
    (Array.isArray(fileList) ? fileList : [fileList]).forEach(f => fd.append('files', f));
    const { data } = await http.post('/ai/extract-datasheet', fd, { timeout: 120000 });
    // Extraction runs as a background job so a slow document can't hit the
    // 60s gateway timeout — poll until it finishes.
    const started = Date.now();
    for (;;) {
      await new Promise(r => setTimeout(r, 2500));
      const { data: s } = await http.get('/ai/extract-status/' + data.job_id, { timeout: 30000 });
      if (s.status === 'done') return s;
      if (onStage && s.stage) onStage(s.stage);
      if (Date.now() - started > 6 * 60 * 1000) throw new Error('The documents took too long to read — try fewer files at once');
    }
  },
  async aiMatchQuote(file, items) {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('items', JSON.stringify(items));
    const { data } = await http.post('/ai/match-quote', fd, { timeout: 240000 });
    return data;
  },
  async aiSuggestNames(sheet) {
    const { data } = await http.post('/ai/suggest-names', { sheet }, { timeout: 120000 });
    return data;
  },

  // ---- products ----
  async listProducts({ scope, q, owner_id, limit = 500, offset = 0 } = {}) {
    if (DEMO) {
      let items = LS.get(DKEY.products, []);
      if (scope) items = items.filter(p => p.scope === scope);
      if (owner_id) items = items.filter(p => p.owner_id === owner_id);
      if (q) { const terms = q.toLowerCase().split(/\s+/).filter(Boolean); items = items.filter(p => { const t = searchText(p); return terms.every(x => t.includes(x)); }); }
      items.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
      return { items: items.slice(offset, offset + limit), total: items.length };
    }
    const { data } = await http.get('/products', { params: { scope, q, owner_id, limit, offset } });
    return data;
  },
  async createProduct(input) {
    if (DEMO) {
      const u = demoUser(); const items = LS.get(DKEY.products, []);
      const row = { id: uid('p_'), owner_id: u.id, scope: input.scope || 'customer', netsuite_item_id: null, netsuite_url: null, name: input.name || '(Untitled)', code: input.code || '', data: input.data || {}, created_at: now(), updated_at: now() };
      items.push(row); LS.set(DKEY.products, items); demoAudit('product.create', 'product', row.id, { name: row.name, scope: row.scope });
      return row;
    }
    const { data } = await http.post('/products', input); return data;
  },
  async updateProduct(id, patch) {
    if (DEMO) {
      const items = LS.get(DKEY.products, []); const it = items.find(p => p.id === id); if (!it) throw new Error('not found');
      Object.assign(it, patch, { updated_at: now() }); LS.set(DKEY.products, items); demoAudit('product.update', 'product', id, { name: it.name });
      return it;
    }
    const { data } = await http.patch('/products/' + id, patch); return data;
  },
  async deleteProduct(id) {
    if (DEMO) { LS.set(DKEY.products, LS.get(DKEY.products, []).filter(p => p.id !== id)); demoAudit('product.delete', 'product', id, {}); return { ok: true }; }
    const { data } = await http.delete('/products/' + id); return data;
  },
  async bulkProducts(ids, action, scope) {
    if (DEMO) {
      let items = LS.get(DKEY.products, []);
      if (action === 'delete') items = items.filter(p => ids.indexOf(p.id) < 0);
      else items.forEach(p => { if (ids.indexOf(p.id) >= 0) { p.scope = scope; p.updated_at = now(); } });
      LS.set(DKEY.products, items); demoAudit('product.bulk_' + action, 'product', null, { count: ids.length, scope });
      return { updated: ids.length };
    }
    const { data } = await http.post('/products/bulk', { ids, action, scope }); return data;
  },

  // ---- projects ----
  async listProjects(opts = {}) {
    if (DEMO) return { items: LS.get(DKEY.projects, []) };
    const { data } = await http.get('/projects', { params: opts.all ? { all: true } : {} }); return data;
  },
  async createProject(input) {
    if (DEMO) { const u = demoUser(); const items = LS.get(DKEY.projects, []); const row = { id: uid('pj_'), owner_id: u.id, name: input.name || 'Project', client: '', description: '', cover_sheet: input.cover_sheet || null, items: [], created_at: now(), updated_at: now() }; items.push(row); LS.set(DKEY.projects, items); demoAudit('project.create', 'project', row.id, { name: row.name }); return row; }
    const { data } = await http.post('/projects', input); return data;
  },
  async updateProject(id, patch) {
    if (DEMO) { const items = LS.get(DKEY.projects, []); const it = items.find(p => p.id === id); Object.assign(it, patch, { updated_at: now() }); LS.set(DKEY.projects, items); return it; }
    const { data } = await http.patch('/projects/' + id, patch); return data;
  },
  async deleteProject(id) {
    if (DEMO) { LS.set(DKEY.projects, LS.get(DKEY.projects, []).filter(p => p.id !== id)); demoAudit('project.delete', 'project', id, {}); return { ok: true }; }
    const { data } = await http.delete('/projects/' + id); return data;
  },
  async updateProjectMembers(id, action, user_id) {
    if (DEMO) { const items = LS.get(DKEY.projects, []); const it = items.find(p => p.id === id); if (it) { const m = new Set(it.members || []); action === 'add' ? m.add(user_id) : m.delete(user_id); it.members = [...m]; LS.set(DKEY.projects, items); } return it; }
    const { data } = await http.patch('/projects/' + id + '/members', { action, user_id }); return data;
  },
  async projectOppNames(ids) {
    if (DEMO || !ids || !ids.length) return { items: [] };
    const { data } = await http.post('/projects/opp-names', { ids }); return data;
  },

  async listProjectUsers() {
    if (DEMO) return { items: LS.get(DKEY.users, DEMO_USERS) };
    const { data } = await http.get('/project-users'); return data;
  },

  // ---- users / allowed / audit ----
  async listUsers() { if (DEMO) return { items: LS.get(DKEY.users, DEMO_USERS) }; const { data } = await http.get('/users'); return data; },
  async setUserPerms(id, overrides) { const { data } = await http.patch('/users/' + id + '/perms', { overrides }); return data; },
  async setUserRole(id, role) { if (DEMO) { const us = LS.get(DKEY.users, DEMO_USERS); const u = us.find(x => x.id === id); if (u) u.role = role; LS.set(DKEY.users, us); demoAudit('user.role_change', 'user', id, { role }); return u; } const { data } = await http.patch('/users/' + id + '/role', { role }); return data; },
  async setUserRegion(id, region) { if (DEMO) { const us = LS.get(DKEY.users, DEMO_USERS); const u = us.find(x => x.id === id); if (u) u.region = region; LS.set(DKEY.users, us); return u; } const { data } = await http.patch('/users/' + id + '/region', { region }); return data; },
  async listAllowed() { if (DEMO) return { items: LS.get(DKEY.allowed, []) }; const { data } = await http.get('/allowed'); return data; },
  async addAllowed(email, role, name, region) { if (DEMO) { const a = LS.get(DKEY.allowed, []); a.push({ email: email.toLowerCase(), role, name, region: region || 'AU' }); LS.set(DKEY.allowed, a); demoAudit('allowlist.add', 'allowed_email', null, { email, role }); return { email, role, name, temp_password: 'demo12345' }; } const { data } = await http.post('/allowed', { email, role, name, region: region || 'AU' }); return data; },
  async changePassword(new_password) { const { data } = await http.post('/auth/change-password', { new_password }); if (data.token) setToken(data.token); return data; },
  async resetAllowedPassword(email) { const { data } = await http.post('/allowed/' + encodeURIComponent(email) + '/reset-password'); return data; },
  async setAllowedRole(email, role) { if (DEMO) { const a = LS.get(DKEY.allowed, []); const e = a.find(x => x.email === email.toLowerCase()); if (e) e.role = role; LS.set(DKEY.allowed, a); return e; } const { data } = await http.patch('/allowed/' + encodeURIComponent(email), { role }); return data; },
  async removeAllowed(email) { if (DEMO) { LS.set(DKEY.allowed, LS.get(DKEY.allowed, []).filter(x => x.email !== email.toLowerCase())); demoAudit('allowlist.remove', 'allowed_email', null, { email }); return { ok: true }; } const { data } = await http.delete('/allowed/' + encodeURIComponent(email)); return data; },
  async listAudit({ limit = 500, offset = 0, q, category } = {}) { if (DEMO) { let a = LS.get(DKEY.audit, []).slice().reverse(); if (q) a = a.filter(e => JSON.stringify(e).toLowerCase().includes(q.toLowerCase())); return { items: a.slice(offset, offset + limit), total: a.length }; } const { data } = await http.get('/audit', { params: { limit, offset, q, category } }); return data; },
  async logEvent(action, entity_type = '', entity_id = '', metadata = null) { if (DEMO) { demoAudit(action, entity_type, entity_id, metadata || {}); return; } try { await http.post('/audit/event', { action, entity_type, entity_id, metadata }); } catch (e) { /* best-effort */ } },

  // ---- netsuite ----
  async nsStatus() { if (DEMO) return { configured: false, account_id: null }; const { data } = await http.get('/netsuite/status'); return data; },
  async nsCreateItem(fields) { const { data } = await http.post('/netsuite/create-item', { fields }); return data; },
  async nsSearch(q) { const { data } = await http.get('/netsuite/search', { params: { q } }); return data; },
  async productSearchNs(q, filters = {}, limit) { const { data } = await http.get('/product-search/netsuite', { params: { q, ...filters, limit: limit || undefined }, timeout: 90000 }); return data; },
  async productSearchDs(q, filters = {}, limit) { const { data } = await http.get('/product-search/datasheets', { params: { q, ...filters, limit: limit || undefined } }); return data; },
  async productSearchFilters() { const { data } = await http.get('/product-search/filters'); return data; },
  async psItemDetails(id) { const { data } = await http.get('/product-search/item-details/' + id, { timeout: 60000 }); return data; },
  async dashboard() { const { data } = await http.get('/dashboard'); return data; },
  async getProduct(id) { const { data } = await http.get('/products/' + id); return data; },
  async nsItem(id) { const { data } = await http.get('/netsuite/item/' + id); return data; },
  async nsItemFields(id) { const { data } = await http.get('/netsuite/item-fields/' + id); return data; },
  async nsVendors(q) { const { data } = await http.get('/netsuite/vendors', { params: { q } }); return data; },
  async nsPush(body) { const { data } = await http.post('/netsuite/push', body); return data; },
  async nsPushes() { const { data } = await http.get('/netsuite/pushes'); return data; },
  async nsUpdatePricing(body) { const { data } = await http.post('/netsuite/update-pricing', body); return data; },
  async skusParse(file) { const fd = new FormData(); fd.append('file', file); const { data } = await http.post('/skus/parse', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); return data; },
  async skusMatch(skus) { const { data } = await http.post('/skus/match', { skus }); return data; },
  async skusCreateDatasheets(records) { const { data } = await http.post('/skus/create-datasheets', { records }, { timeout: 180000 }); return data; },
  async skusPushNetsuite(body) { const { data } = await http.post('/skus/push-netsuite', body, { timeout: 120000 }); return data; },
  async skusLists() { const { data } = await http.get('/skus/lists'); return data; },
  async skusSave(body) { const { data } = await http.post('/skus/lists', body); return data; },
  async skusDelete(id) { const { data } = await http.delete('/skus/lists/' + id); return data; },
  async listQuotes(params) { const { data } = await http.get('/quotes', { params: params || {} }); return data; },
  async saveQuote(body) { const { data } = await http.post('/quotes', body); return data; },
  async updateQuote(id, body) { const { data } = await http.put(`/quotes/${id}`, body); return data; },
  async deleteQuote(id) { const { data } = await http.delete(`/quotes/${id}`); return data; },
  async nsOpportunity(number) { const { data } = await http.get('/netsuite/opportunity/' + encodeURIComponent(number)); return data; },
  async nsSalesReps() { const { data } = await http.get('/netsuite/salesreps'); return data; },
  async nsCreateQuote(body) { const { data } = await http.post('/netsuite/create-quote', body); return data; },
  async nsOppQuotes(number) { const { data } = await http.get('/netsuite/opportunity/' + encodeURIComponent(number) + '/quotes'); return data; },
  async nsEstimateLines(id) { const { data } = await http.get('/netsuite/estimate/' + id + '/lines'); return data; },
  async nsQuoteByNumber(tranid) { const { data } = await http.get('/netsuite/quote/' + encodeURIComponent(tranid)); return data; },
  async adminWipeData() { const { data } = await http.post('/admin/wipe-data'); return data; },
  async listApprovals(mine) { const { data } = await http.get('/approvals', { params: mine ? { mine: 1 } : {} }); return data; },
  async approveDatasheet(id, notes) { const { data } = await http.post('/approvals/' + id + '/approve', { notes }); return data; },
  async denyDatasheet(id, notes) { const { data } = await http.post('/approvals/' + id + '/deny', { notes }); return data; },
  async ordersFinance(segment, subsidiary) { const { data } = await http.get('/sales-intel/orders-finance', { params: { segment: segment || undefined, subsidiary: subsidiary || undefined } }); return data; },
  async winProbability(subsidiary) { const { data } = await http.get('/sales-intel/win-probability', { params: { subsidiary: subsidiary || undefined } }); return data; },
  async winProbabilitySync() { const { data } = await http.post('/sales-intel/opps/sync'); return data; },
  async winProbabilityExport(subsidiary) { const { data } = await http.get('/sales-intel/win-probability/export', { params: { subsidiary: subsidiary || undefined }, responseType: 'blob', timeout: 120000 }); return data; },
  async salesIntelSubsidiaries() { const { data } = await http.get('/sales-intel/subsidiaries'); return data; },
  async salesIntelAlerts(subsidiary) { const { data } = await http.get('/sales-intel/alerts', { params: { subsidiary: subsidiary || undefined } }); return data; },
  async financePackExport(month, segment, subsidiary) {
    const { data } = await http.get('/sales-intel/finance-pack', {
      params: { month, segment: segment || undefined, subsidiary: subsidiary || undefined },
      responseType: 'blob', timeout: 180000,
    });
    return data;
  },
  async spUploadDatasheet(bytes, filename) {
    const fd = new FormData();
    fd.append('file', new Blob([bytes], { type: 'application/pdf' }), filename);
    const { data } = await http.post('/sharepoint/upload-datasheet', fd, { timeout: 180000 });
    return data;
  },
  async spSyncArchive() {
    const { data } = await http.post('/sharepoint/sync-archive', null, { timeout: 300000 });
    return data;
  },

  // ---- legacy datasheets (migration workbench) ----
  async dsCheck({ code, name, ns_item_id } = {}) {
    const { data } = await http.get('/datasheet-check', { params: { code: code || undefined, name: name || undefined, ns_item_id: ns_item_id || undefined } });
    return data;
  },
  async salesIntelSummary(days, items, segment, subsidiary) {
    const { data } = await http.get('/sales-intel/summary', { params: { days, items: items || undefined, segment: segment || undefined, subsidiary: subsidiary || undefined } }); return data;
  },
  async salesIntelStatus() {
    const { data } = await http.get('/sales-intel/status'); return data;
  },
  async salesIntelSync(mode) {
    const { data } = await http.post('/sales-intel/sync', { mode }); return data;
  },
  async salesIntelRoles() {
    const { data } = await http.get('/sales-intel/roles'); return data;
  },
  async salesIntelForecast(segment, subsidiary, period, items, buffer) {
    const { data } = await http.get('/sales-intel/forecast', { params: { segment: segment || undefined, subsidiary: subsidiary || undefined, period: period || undefined, items: items || undefined, buffer: buffer != null ? buffer : undefined } }); return data;
  },
  async salesIntelItemsBreakdown(items, period, segment, subsidiary) {
    const { data } = await http.get('/sales-intel/items-breakdown', { params: { items: items.join('|'), period, segment: segment || undefined, subsidiary: subsidiary || undefined }, timeout: 90000 }); return data;
  },
  async salesIntelItemSearch(q, period) { const { data } = await http.get('/sales-intel/item-search', { params: { q, period: period || undefined }, timeout: 60000 }); return data; },
  async salesIntelItemDetail(item, period, segment, subsidiary) {
    const { data } = await http.get('/sales-intel/item-detail', { params: { item, period: period || undefined, segment: segment || undefined, subsidiary: subsidiary || undefined } }); return data;
  },
  async salesIntelRep(name, role, days, segment, subsidiary) {
    const { data } = await http.get('/sales-intel/rep', { params: { name, role, days, segment: segment || undefined, subsidiary: subsidiary || undefined } }); return data;
  },
  async salesIntelClient(name, days, segment, subsidiary) {
    const { data } = await http.get('/sales-intel/client', { params: { name, days, segment: segment || undefined, subsidiary: subsidiary || undefined } }); return data;
  },
  async salesIntelStale(daysIdle, segment, subsidiary) {
    const { data } = await http.get('/sales-intel/stale', { params: { days_idle: daysIdle || 30, segment: segment || undefined, subsidiary: subsidiary || undefined } }); return data;
  },
  async salesIntelPersonal(period) {
    const { data } = await http.get('/sales-intel/personal', { params: { period: period || '90' } }); return data;
  },
  async setUserNsRep(id, nsRep) {
    const { data } = await http.patch(`/users/${id}/nsrep`, { ns_rep: nsRep }); return data;
  },
  async setUserName(id, name) {
    const { data } = await http.patch(`/users/${id}/name`, { name }); return data;
  },
  async nsOauthStatus() { const { data } = await http.get('/netsuite/oauth/status'); return data; },
  async nsOauthStart() { const { data } = await http.post('/netsuite/oauth/start'); return data; },
  async nsOauthDisconnect() { const { data } = await http.post('/netsuite/oauth/disconnect'); return data; },
  async nsOauthConnections() { const { data } = await http.get('/netsuite/oauth/connections'); return data; },
  async nsOauthResetAll() { const { data } = await http.post('/netsuite/oauth/reset-all'); return data; },
  async auditExport({ category, q } = {}) {
    const { data } = await http.get('/audit/export', {
      params: { category: category || undefined, q: q || undefined },
      responseType: 'blob', timeout: 120000,
    });
    return data;
  },
  async salesIntelInsights(days, segment, subsidiary) {
    const { data } = await http.get('/sales-intel/insights', { params: { days, segment: segment || undefined, subsidiary: subsidiary || undefined } }); return data;
  },
  async salesIntelDrillExport(kind, name, role, days, segment, subsidiary, view) {
    const { data } = await http.get('/sales-intel/drill-export', {
      params: { kind, name, role, days, segment: segment || undefined, subsidiary: subsidiary || undefined, view: view || undefined },
      responseType: 'blob', timeout: 120000,
    });
    return data;
  },
  async salesIntelItemForecastExport(period, segment, subsidiary, buffer) {
    const { data } = await http.get('/sales-intel/item-forecast-export', { params: { period: period || undefined, segment: segment || undefined, subsidiary: subsidiary || undefined, buffer: buffer != null ? buffer : undefined }, responseType: 'blob', timeout: 180000 });
    return data;
  },
  async salesIntelExport(days, segment, subsidiary) {
    const { data } = await http.get('/sales-intel/export', { params: { days, segment: segment || undefined, subsidiary: subsidiary || undefined }, responseType: 'blob', timeout: 120000 });
    return data;
  },
  async saveSalesIntelRoles(items) {
    const { data } = await http.put('/sales-intel/roles', { items }); return data;
  },
  async listLegacy(q) {
    if (DEMO) { const ql = (q || '').toLowerCase(); return { items: demoLegacy.map(({ blobUrl, ...m }) => m).filter(m => !ql || (m.name || '').toLowerCase().includes(ql)) }; }
    const { data } = await http.get('/legacy-datasheets', { params: q ? { q } : {} }); return data;
  },
  async uploadLegacy(file, name, source = 'upload') {
    if (DEMO) {
      const meta = { id: uid('lg_'), name: name || file.name, source, content_type: file.type, pages: null, size: file.size, uploaded_by: (demoUser() || {}).id, created_at: now() };
      demoLegacy.push({ ...meta, blobUrl: URL.createObjectURL(file), _file: file });
      demoAudit('legacy.upload', 'legacy', meta.id, { name: meta.name });
      return meta;
    }
    const fd = new FormData(); fd.append('file', file); fd.append('name', name || file.name); fd.append('source', source);
    const { data } = await http.post('/legacy-datasheets', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data;
  },
  legacyFileUrl(id) {
    if (DEMO) { const it = demoLegacy.find(x => x.id === id); return it ? it.blobUrl : ''; }
    return BACKEND + '/api/legacy-datasheets/' + id + '/file';
  },
  // Returns an object URL the canvas/img can render (auth-safe in live mode).
  async getLegacyObjectUrl(id) {
    if (DEMO) { const it = demoLegacy.find(x => x.id === id); return it ? it.blobUrl : ''; }
    const { data } = await http.get('/legacy-datasheets/' + id + '/file', { responseType: 'blob' });
    return URL.createObjectURL(data);
  },
  legacyContentType(id) {
    if (DEMO) { const it = demoLegacy.find(x => x.id === id); return it ? it.content_type : ''; }
    return '';
  },
  async deleteLegacy(id) {
    if (DEMO) { demoLegacy = demoLegacy.filter(x => x.id !== id); return { ok: true }; }
    const { data } = await http.delete('/legacy-datasheets/' + id); return data;
  },
  // ---- families (Core Range grouping) ----
  async listFamilies() {
    if (DEMO) return { items: LS.get('lpa_demo_families', []) };
    const { data } = await http.get('/families'); return data;
  },
  async createFamily(name) {
    if (DEMO) { const items = LS.get('lpa_demo_families', []); const row = { id: uid('fam_'), name, created_at: now() }; items.push(row); LS.set('lpa_demo_families', items); return row; }
    const { data } = await http.post('/families', { name }); return data;
  },
  async deleteFamily(id) {
    if (DEMO) { LS.set('lpa_demo_families', LS.get('lpa_demo_families', []).filter(f => f.id !== id)); return { ok: true }; }
    const { data } = await http.delete('/families/' + id); return data;
  },
};
